import dayjs from 'dayjs';

/**
 * Plan display derivation for the console.
 *
 * The backend's `plan.status` is the source of truth for WHICH state a plan is in
 * (phase 2 derives it from UserPlan; it is never stored). This module only refines
 * `active`/`trial` with the days remaining, so a plan expiring inside a week reads
 * as a warning rather than a healthy green — the difference between "this customer
 * is fine" and "this customer calls you on Monday".
 */

/** A plan inside this many days reads amber rather than green. */
export const EXPIRY_WARNING_DAYS = 7;

/** `to: null` means never expires (D8) — NOT "no expiry recorded". */
export const neverExpires = (current) => Boolean(current) && current.to === null;

/**
 * Whole days from now until `to`, rounded DOWN — so a plan with 7 days and 23
 * hours left reads as 7, not 8. Rounding up would let a plan display "8d" on its
 * last day but one, which is the day someone decides not to call the customer.
 */
export const daysUntil = (to, now = new Date()) =>
  to == null ? null : dayjs(to).diff(dayjs(now), 'day');

/**
 * The badge a row shows. Returns { tone, label }.
 *
 * `tone` is one of grey | green | amber | red and is what the badge colours by;
 * the component never re-derives it from the label.
 */
export const planBadge = (plan, now = new Date()) => {
  const status = plan?.status ?? 'none';
  const current = plan?.current ?? null;

  if (status === 'n/a') return { tone: 'grey', label: 'N/A' };
  if (status === 'none') return { tone: 'grey', label: 'No plan' };
  if (status === 'expired') return { tone: 'red', label: 'Expired' };

  const noun = status === 'trial' ? 'Trial' : 'Active';

  if (neverExpires(current)) return { tone: 'green', label: `${noun} · never expires` };

  const days = daysUntil(current?.to, now);
  // A missing `to` on an active plan should not happen, but rendering "NaNd"
  // if it does would be worse than dropping the suffix.
  if (days === null) return { tone: 'green', label: noun };

  // The backend said active, the date says otherwise — trust the date. The two
  // disagree only when the row was fetched before the plan lapsed, and a stale
  // green badge is the one that gets someone told their account is fine.
  if (days < 0) return { tone: 'red', label: 'Expired' };

  return {
    tone: days <= EXPIRY_WARNING_DAYS ? 'amber' : 'green',
    label: `${noun} · ${days}d`,
  };
};

/** Org types that hold no plan. */
export const isPlanlessOrgType = (type) => type === 'consultant' || type === 'verifier';

/** Expiry cell text: a date, "Never", or an em dash for entities with no plans. */
export const expiryText = (type, plan) => {
  if (isPlanlessOrgType(type)) return '—';
  const current = plan?.current;
  if (!current) return '—';
  if (neverExpires(current)) return 'Never';
  return dayjs(current.to).format('DD MMM YYYY');
};

/** Plan-name cell text; consultants have no plans at all (phase 2). */
export const planNameText = (type, plan) => {
  if (isPlanlessOrgType(type)) return '—';
  return plan?.current?.name ?? '—';
};

/**
 * Price text, in the organization's DERIVED currency (backend D22), or "Not recorded".
 *
 * `null` is NOT free: the backend keeps an unrecorded price distinct from a recorded 0, and so
 * does this — 0 renders as a price. Whole amounts drop the decimals ("€2,500"), the way the
 * pricing page writes them.
 */
export const priceText = (plan) => {
  const price = plan?.current?.price;
  if (price === null || price === undefined) return 'Not recorded';
  if (!plan?.currency) return String(price);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: plan.currency,
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
  }).format(price);
};

/**
 * What the Manage-plan request says about price — `undefined` to leave the key out (D21, D23).
 *
 * Omitting `price` is not "no change": the API CARRIES the current price when the plan is
 * unchanged and CLEARS it when the plan changes. So the key is sent exactly when omission would
 * store something other than what the field shows — and the request can never disagree with it.
 *
 * `typed` is the parsed field: a number, or null for an empty one.
 */
export const priceForRequest = ({ typed, currentPrice, planChanged }) => {
  const byOmission = planChanged ? null : (currentPrice ?? null);
  return typed === byOmission ? undefined : typed;
};

/**
 * Where a preset (+30d, +1y…) lands: the later of TODAY and the current expiry.
 *
 * Stacking rather than resetting is the point — extending a plan with 10 days left
 * by 30 should give 40, not 30. Anchoring on today alone would silently shorten
 * every early renewal, which is the kind of bug nobody reports because the number
 * still looks plausible.
 */
export const addPreset = (current, { days = 0, months = 0, years = 0 }, now = new Date()) => {
  const today = dayjs(now);
  const currentTo = current?.to ? dayjs(current.to) : null;
  const anchor = currentTo && currentTo.isAfter(today) ? currentTo : today;
  return anchor.add(days, 'day').add(months, 'month').add(years, 'year').toDate();
};

export const PRESETS = [
  { label: '+7d', delta: { days: 7 } },
  { label: '+14d', delta: { days: 14 } },
  { label: '+30d', delta: { days: 30 } },
  { label: '+3mo', delta: { months: 3 } },
  { label: '+6mo', delta: { months: 6 } },
  { label: '+1y', delta: { years: 1 } },
];
